import { axiosApi } from "./fetch";
import type { AxiosRequestConfig } from "axios";
import { config } from "../config";
import type { FormikErrors, FormikTouched, FormikValues } from "formik";
import Guid from "guid";
import { isObject } from "lodash";
import type { Error } from "../types";

export const localStorageKey = "unauthorizedAccessToken";
export const localStorageFactoryId = "factoryId";
export const localStorageValidateOtpId = "validateOtpId";

export const getErrorString = (error: unknown): string => {
  if (typeof error === "string") return error;
  if (Array.isArray(error)) return error.join("\n");
  if (error && typeof error === "object") return JSON.stringify(error);
  return "";
};

export const getFormikErrors = (
  errors: FormikErrors<FormikValues>,
  touched?: FormikTouched<FormikValues>,
): Error[] => {
  if (errors) {
    return Object.keys(errors)
      .filter((key) => touched?.[key] && errors[key])
      .map((key) => ({
        id: key,
        message: getErrorString(errors[key]),
      }));
  }
  return [];
};
//   if (errorCode in ErrorMessagesOTP) {
//     return {
//       id: errorCode,
//       message: getErrorString(
//         ErrorMessagesOTP[errorCode as keyof typeof ErrorMessagesOTP],
//       ),
//     };
//   }
//
//   if (errorCode in ErrorMessagesCreateAccount) {
//     return {
//       id: errorCode,
//       message: getErrorString(
//         ErrorMessagesCreateAccount[
//           errorCode as keyof typeof ErrorMessagesCreateAccount
//         ],
//       ),
//     };
//   }
//
//   if (errorCode in ErrorCodes) {
//     return {
//       id: errorCode,
//       message: getErrorString(ErrorCodes[errorCode as keyof typeof ErrorCodes]),
//     };
//   }
//
//   return {
//     id: "generic-error",
//     message: getErrorString(ErrorMessages.genericError),
//   };
// };
//
// export const getResponseError = (error: {
//   errorDetails?: { code?: string };
// }): Error | null => {
//   if (error?.errorDetails?.code) {
//     return getErrorByErrorCode(error.errorDetails.code);
//   }
//   return null;
// };

/**
 * Helper function to extract error code from API error object
 */
export const extractErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== "object") {
    return null;
  }

  const errorObj = error as {
    data?: { errorDetails?: { code?: string } };
    response?: { data?: { errorDetails?: { code?: string } } };
    errorDetails?: { code?: string };
  };

  const errorDetails =
    errorObj?.data?.errorDetails ??
    errorObj?.response?.data?.errorDetails ??
    errorObj?.errorDetails;

  return errorDetails?.code ?? null;
};

/**
 * Extract error message for snackbar display from error object
 */
export const getSnackBarError = (error: {
  errorDetails?: { message?: unknown; reason?: unknown };
}): string => {
  if (error?.errorDetails?.message) {
    return getErrorString(error.errorDetails.message);
  }
  if (error?.errorDetails?.reason) {
    return getErrorString(error.errorDetails.reason);
  }
  if (error) {
    return getErrorString("An error occurred. Please try again.");
  }
  return "";
};

export const addTokenToLocalStorage = (): void => {
  const correlationId = Guid.create().value;
  const guid = Guid.create().value;

  const unAuthHeaders = {
    "x-correlation-id": correlationId.toString(),
    client_scope_identifier: "Web",
    guid: guid.toString(),
    "x-api-key": config.env("xAPIKey"),
  };

  const axiosConfig: AxiosRequestConfig<{
    isHeadersPassed: boolean;
    method: string;
    url: string;
    headers: Record<string, string>;
    params: Record<string, unknown>;
  }> = {
    method: "get",
    url: `${config.env("baseUrl")}/unauth/customers/token`,
    headers: unAuthHeaders,
    params: { isHeadersPassed: true },
  };

  axiosApi(axiosConfig)
    .then((response) => {
      if (response?.response?.accessToken) {
        localStorage.setItem(localStorageKey, response.response.accessToken);
      }
    })
    .catch((error) => {
      console.error("Error fetching unauthorized token:", error);
    });
};

export const cleanLocalStorage = (): void => {
  if (localStorage.getItem(localStorageKey)) {
    localStorage.removeItem(localStorageKey);
  }

  if (localStorage.getItem(localStorageFactoryId)) {
    localStorage.removeItem(localStorageFactoryId);
  }

  if (localStorage.getItem(localStorageValidateOtpId)) {
    localStorage.removeItem(localStorageValidateOtpId);
  }
};

export const maskMobile = (mobile: string): string => {
  if (!mobile || typeof mobile !== "string") {
    return mobile;
  }
  const digits = mobile.replace(/\D/g, "");
  if (digits.length < 4) {
    return mobile;
  }
  const first = digits[0];
  const lastTwo = digits.slice(-2);
  return `(${first}**) ***-**${lastTwo}`;
};

export const maskEmail = (email: string): string => {
  if (!email || typeof email !== "string") {
    return email;
  }
  const idx = email.indexOf("@");
  const first = email[0];
  const endOfEmail = idx > 3 ? email.slice(idx - 1) : email.slice(idx);
  return `${first}******${endOfEmail}`;
};

export const fetchRequest = async <T = unknown>(
  config: AxiosRequestConfig,
): Promise<{ response: T | undefined; error: unknown }> => {
  const data = await axiosApi(config);
  return { response: data?.response, error: data?.error?.data ?? null };
};

const getHeaders = () => ({
  Authorization: "Bearer " + localStorage.getItem(localStorageKey),
  "x-correlation-id": Guid.create().value,
  client_scope_identifier: "Web",
  guid: Guid.create().value,
  "x-api-key": config.env("xAPIKey"),
});

export const createRequestConfig = (
  data: Record<string, unknown>,
  url: string,
  method: string,
): AxiosRequestConfig => {
  const headers = getHeaders();

  return {
    url,
    data,
    method,
    headers,
    params: { isHeadersPassed: true },
  };
};

export const getOTPEmailRequestConfig = (
  email: string,
  id?: string,
): AxiosRequestConfig => {
  const url = `${config.env("baseUrl")}/unauth/customers/trigger-otp`.replace(
    "/api/v1/",
    "/api/v2/",
  );
  let requestData: Record<string, unknown> = {
    factor: {
      factorType: "EMAIL",
      email,
    },
  };
  if (id) {
    requestData = {
      factor: {
        factorType: "EMAIL",
        email,
        id,
      },
    };
  }
  return createRequestConfig(requestData, url, "post");
};

export const sendOneTimePassword = async (
  email: string,
  id?: string,
): Promise<{ response: unknown; error: unknown }> => {
  const RequestConfig = getOTPEmailRequestConfig(email, id);
  const data = await fetchRequest(RequestConfig);
  return data;
};

export const getValuesFromResponse = (
  obj: unknown,
): {
  id?: string;
  factorId?: string;
  code?: string;
  message?: string;
} => {
  const result: {
    id?: string;
    factorId?: string;
    code?: string;
    message?: string;
  } = {};

  function search(o: unknown) {
    if (!isObject(o)) return;
    for (const [key, value] of Object.entries(o)) {
      switch (key) {
        case "code":
          result.code = value;
          break;
        case "message":
          result.message = value;
          break;
        case "id":
          result.id = value;
          break;
        case "factorId":
          result.factorId = value;
          break;
      }
      if (isObject(value)) {
        search(value);
      }
    }
  }

  search(obj);
  return result;
};

export const timeDelay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const findPasswordInEmail = (
  email: string,
  password: string,
): boolean => {
  if (!email || !password) return false;
  let maxMatchLength = 0;

  for (let i = 0; i < email.length - 1; i++) {
    const result = findStringFromStart(email.substring(i), password);
    if (result && result.length > maxMatchLength) {
      maxMatchLength = result.length;
      if (maxMatchLength >= email.length - i) break;
    }
  }

  return maxMatchLength > 3;
};

const findStringFromStart = (a: string, b: string) => {
  for (let i = a.length; i > 0; i--) {
    const d = a.substring(0, i);
    const j = b.indexOf(d);
    if (j >= 0) {
      return { position: j, length: i };
    }
  }
  return null;
};

/** Hostnames permitted for `registerNowUrl` from runtime config (https only). */
const REGISTER_NOW_URL_ALLOWED_HOSTS: readonly string[] = [
  "account-dev.freedommortgage.com",
  "account-test.freedommortgage.com",
  "account-uat.freedommortgage.com",
  "account.freedommortgage.com",
];

/**
 * Validates `registerNowUrl` before assigning `window.location` to prevent open redirects.
 */
export const isValidRegisterNowUrl = (url: string): boolean => {
  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:") {
      return false;
    }
    if (parsed.username || parsed.password) {
      return false;
    }
    return REGISTER_NOW_URL_ALLOWED_HOSTS.includes(parsed.hostname);
  } catch {
    return false;
  }
};

/**
 * Validates if a return URL is safe for redirect
 * Only allows relative URLs, same-origin URLs, localhost (for development), or trusted Okta/identity domains
 */
export const isValidReturnUrl = (url: string): boolean => {
  if (!url) {
    return false;
  }
  
  try {
    // Allow relative URLs
    if (url.startsWith('/')) {
      return true;
    }
    
    // Parse the URL
    const returnUrl = new URL(url, window.location.origin);
    const currentOrigin = new URL(window.location.href);
    
    // Allow same origin redirects
    if (returnUrl.origin === currentOrigin.origin) {
      return true;
    }
    
    // Allow localhost redirects for development (any port)
    if (returnUrl.hostname === 'localhost' || 
        returnUrl.hostname === '127.0.0.1' || 
        returnUrl.hostname.endsWith('.local')) {
      return true;
    }
    
    // Allow trusted freedommortgage.com domains (Okta, identity server, dashboard)
    const trustedDomains = [
      'identity-dev.freedommortgage.com',
      'identity-test.freedommortgage.com',
      'identity-uat.freedommortgage.com',
      'identity.freedommortgage.com',
      'mylogin-dev.freedommortgage.com',
      'mylogin-test.freedommortgage.com',
      'mylogin-uat.freedommortgage.com',
      'mylogin.freedommortgage.com',
      ...REGISTER_NOW_URL_ALLOWED_HOSTS,
      'myfreedomtest.freedommortgage.com',
      'myfreedomuat.freedommortgage.com',
      'myfreedom.freedommortgage.com',
    ];
    
    return trustedDomains.includes(returnUrl.hostname);
  } catch (error) {
    // If URL parsing fails, reject it
    return false;
  }
};
